import React, { useEffect, useState } from "react";
import api from "../../../api/client";
import { formatBRL, formatDateBR } from "../../../utils/format";
import { employeesText, evaluationUnitText, hoursText, technicalVisitsText } from "../../../utils/commercialText";
import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Button } from "../../../components/ui/button";
import { Sparkles, Plus, X, Eye, Users, BadgeDollarSign, ShieldCheck, FileCheck2 } from "lucide-react";
import { toast } from "sonner";

const chemicalCommercialName = (name) => {
  const clean = String(name || "").trim();
  if (!clean) return "Avaliação química quantitativa";
  const lowered = clean.toLocaleLowerCase("pt-BR");
  if (lowered.startsWith("avaliação química quantitativa de ") || lowered.startsWith("avaliacao quimica quantitativa de ")) return clean;
  const prefixes = ["avaliação química de ", "avaliacao quimica de ", "avaliação quantitativa de ", "avaliacao quantitativa de "];
  const prefix = prefixes.find((p) => lowered.startsWith(p));
  const agent = prefix ? clean.slice(prefix.length).trim() : clean;
  return `Avaliação química quantitativa de ${agent}`;
};

const serviceCommercialName = (item) => String(item?.nome_proposta || item?.descricao || item?.sigla || "").trim();

const fixedCommercialName = (item) => {
  const commercial = String(item?.nome_proposta || "").trim();
  if (commercial) return commercial;
  const clean = String(item?.nome || "").trim();
  if (!clean) return "Avaliação";
  const lowered = clean.toLocaleLowerCase("pt-BR");
  return lowered.startsWith("avaliação ") || lowered.startsWith("avaliacao ") ? clean : `Avaliação de ${clean}`;
};

const ListBlock = ({ title, items, testid }) => (
  <div className="rounded-xl border border-border p-5" data-testid={testid}>
    <div className="mb-3 flex items-center justify-between gap-3">
      <h4 className="label-tech">{title}</h4>
      <span className="text-[11px] text-muted-foreground">{items.length} item(ns)</span>
    </div>
    {items.length === 0 ? (
      <p className="text-sm text-muted-foreground">Nenhum item nesta seção.</p>
    ) : (
      <ul className="space-y-2 text-sm">
        {items.map((it, i) => (
          <li key={`${testid}-${i}-${typeof it === "string" ? it.slice(0, 30) : i}`} className="flex gap-2 leading-relaxed">
            <span className="text-primary">•</span><span>{it}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const ListEditor = ({ title, items, onChange, hint, testid, headerAction }) => {
  const updateItem = (i, value) => {
    const next = [...items];
    next[i] = value;
    onChange(next);
  };
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const addItem = () => onChange([...items, ""]);

  return (
    <div className="rounded-xl border border-border p-5 space-y-3" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="label-tech">{title}</h4>
          <div className="mt-1 text-[11px] text-muted-foreground">{items.length} linha(s) salva(s) na proposta</div>
        </div>
        {headerAction}
      </div>
      {hint && <p className="text-xs leading-relaxed text-muted-foreground">{hint}</p>}
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={`${testid}-${i}`} className="flex gap-2 items-start">
            <span className="text-primary mt-2">•</span>
            <Textarea
              className="flex-1 min-h-[44px] resize-y text-sm"
              rows={1}
              value={it}
              onChange={(e) => updateItem(i, e.target.value)}
              data-testid={`${testid}-item-${i}`}
            />
            <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(i)} aria-label={`Remover item de ${title}`} data-testid={`${testid}-del-${i}`}>
              <X className="size-4" />
            </Button>
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid={`${testid}-add`}>
        <Plus className="size-4 mr-1" /> Adicionar item
      </Button>
    </div>
  );
};

const ToggleCard = ({ checked, onChange, icon: Icon, title, description, disabled, testid }) => (
  <label className={`flex gap-3 rounded-xl border border-border p-4 transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-muted/20"}`}>
    <input type="checkbox" className="mt-1 size-4 accent-primary" checked={!!checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} data-testid={testid} />
    <Icon className="size-5 text-primary mt-0.5 shrink-0" />
    <span>
      <span className="block text-sm font-medium">{title}</span>
      <span className="block text-xs text-muted-foreground mt-1 leading-relaxed">{description}</span>
    </span>
  </label>
);

export default function Step4Review({ data, setData, calc, hideFinancial }) {
  const [defaultsReady, setDefaultsReady] = useState(Boolean(data.presentation_fields_initialized));
  const [aiBusy, setAiBusy] = useState(false);
  const [escopoSug, setEscopoSug] = useState("");

  useEffect(() => {
    if (data.presentation_fields_initialized) {
      setDefaultsReady(true);
      return;
    }
    let active = true;
    api.get("/proposals/text-defaults")
      .then((r) => {
        if (!active) return;
        const defaults = r.data || {};
        setData((current) => {
          if (current.presentation_fields_initialized) return current;
          return {
            ...current,
            inclusos: (current.inclusos || []).length ? current.inclusos : (defaults.inclusos || []),
            nao_inclusos: (current.nao_inclusos || []).length ? current.nao_inclusos : (defaults.nao_inclusos || []),
            prazos: (current.prazos || []).length ? current.prazos : (defaults.prazos || []),
            condicoes_comerciais: (current.condicoes_comerciais || []).length ? current.condicoes_comerciais : (defaults.condicoes_comerciais || []),
            presentation_fields_initialized: true,
          };
        });
        setDefaultsReady(true);
      })
      .catch(() => {
        if (active) {
          setData((current) => ({ ...current, presentation_fields_initialized: true }));
          setDefaultsReady(true);
          toast.error("Não foi possível carregar os textos padrão. As seções podem ser preenchidas manualmente.");
        }
      });
    return () => { active = false; };
  }, [data.presentation_fields_initialized, setData]);

  const setField = (field, value) => setData((current) => ({ ...current, [field]: value }));
  const priceSuffix = (value) => data.exibir_precos_itens && !hideFinancial ? ` — ${formatBRL(value || 0)}` : "";
  const factor = (1 + (Number(data.ajuste_preco_pct || 0) / 100)) * Math.max(0, 1 - (Number(data.desconto_pct || 0) / 100));

  const programas = (data.programas_laudos || []).filter((p) => p.status === "included")
    .map((p) => `${Number(p.quantidade || 1) !== 1 ? `${p.quantidade} × ` : ""}${serviceCommercialName(p)}${priceSuffix((p.venda || 0) * Number(p.quantidade || 1) * factor)}`)
    .filter(Boolean);
  const fixedIn = (data.avaliacoes_fixas || []).filter((f) => f.status === "included")
    .map((f) => {
      const months = f.mensal ? Math.max(data.duracao_meses || 1, 1) : 1;
      const quantity = Number(f.quantidade || 0);
      const quantityLabel = f.por_funcionario ? employeesText(quantity) : evaluationUnitText(quantity, f.unidade || "ponto");
      const suffix = f.mensal ? ` por mês durante ${data.duracao_meses || 1} meses` : "";
      return `${fixedCommercialName(f)} — ${quantityLabel}${suffix}${priceSuffix((f.venda_unitaria || 0) * quantity * months * factor)}`;
    });
  const quimIn = (data.avaliacoes_quimicas || []).filter((q) => q.status === "included")
    .map((q) => `${chemicalCommercialName(q.nome)}${priceSuffix((q.venda || 0) * factor)}`);
  const treinIn = (data.treinamentos || []).filter((t) => t.status === "included")
    .map((t) => {
      const name = serviceCommercialName(t);
      if (!name) return "";
      const hours = Number(t.carga_horaria || 0) > 0 ? ` (carga horária de ${hoursText(t.carga_horaria)})` : "";
      return `${name}${hours}${priceSuffix((t.venda || 0) * factor)}`;
    })
    .filter(Boolean);

  const acomp = [];
  const acc = data.acompanhamento || {};
  const showNum = !!acc.exibir_num_funcionarios;
  const num = Number(acc.num_funcionarios || 0);
  if (acc.incluir_aso !== false && num > 0 && Number(acc.valor_por_funcionario || 0) > 0) {
    const qtd = showNum ? ` — ${employeesText(num)}` : "";
    const accValue = num * Number(acc.valor_por_funcionario || 0) * Math.max(data.duracao_meses || 1, 1) * factor;
    acomp.push(`Gestão de ASO — Admissional, Periódico, Retorno ao Trabalho e Demissional${qtd}${priceSuffix(accValue)}`);
    acomp.push(`Envio de eventos de SST para o eSocial${qtd}`);
  }
  if (Number(acc.visitas_previstas || 0) > 0) {
    const visits = Number(acc.visitas_previstas || 0);
    acomp.push(`${technicalVisitsText(visits)}${priceSuffix(visits * Number(acc.valor_por_visita || 0) * factor)}`);
  }

  const bonus = [
    ...(data.programas_laudos || []).filter((p) => p.status === "bonus").map(serviceCommercialName),
    ...(data.avaliacoes_fixas || []).filter((f) => f.status === "bonus").map(fixedCommercialName),
    ...(data.avaliacoes_quimicas || []).filter((q) => q.status === "bonus").map((q) => chemicalCommercialName(q.nome)),
    ...(data.treinamentos || []).filter((t) => t.status === "bonus").map((t) => {
      const name = serviceCommercialName(t);
      if (!name) return "";
      return Number(t.carga_horaria || 0) > 0 ? `${name} (carga horária de ${hoursText(t.carga_horaria)})` : name;
    }),
  ].filter(Boolean);

  const setAcompPresentation = (checked) => setData((current) => ({
    ...current,
    acompanhamento: { ...current.acompanhamento, exibir_num_funcionarios: checked },
  }));

  const gerarIA = async (campos) => {
    if (!data.client_id) return toast.error("Selecione um cliente antes de gerar com IA.");
    setAiBusy(true);
    try {
      const r = await api.post("/proposals/ai/suggest", { proposal: data, campos });
      const sug = r.data?.sugestoes || {};
      const patch = {};
      if (sug.escopo) { patch.escopo_descricao = sug.escopo; setEscopoSug(sug.escopo); }
      if (sug.observacoes) patch.observacoes = sug.observacoes;
      if (Array.isArray(sug.inclusos)) patch.inclusos = sug.inclusos;
      if (Array.isArray(sug.nao_inclusos)) patch.nao_inclusos = sug.nao_inclusos;
      if (Object.keys(patch).length === 0) return toast.error("A IA não retornou sugestões válidas.");
      setData((current) => ({ ...current, ...patch, presentation_fields_initialized: true }));
      toast.success("Sugestão validada e aplicada.");
    } catch (e) {
      const msg = e.response?.data?.detail || "Falha ao gerar com IA";
      toast.error(typeof msg === "string" ? msg : "Falha ao gerar com IA");
    } finally { setAiBusy(false); }
  };

  const aiBtn = (campos) => (
    <Button type="button" variant="ghost" size="sm" disabled={aiBusy} onClick={() => gerarIA(campos)}>
      <Sparkles className="size-3.5 mr-1" /> IA assistiva
    </Button>
  );

  if (!defaultsReady) {
    return <div className="rounded-2xl border border-border p-6 text-sm text-muted-foreground">Preparando os campos de apresentação da proposta...</div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="wizard-step-4">
      <div>
        <h2 className="text-xl font-medium">Revisão e apresentação comercial</h2>
        <p className="text-sm text-muted-foreground">O conteúdo abaixo é a fonte de verdade da apresentação: o que você editar aqui é salvo na proposta e utilizado no site público e nos documentos.</p>
      </div>

      <div className="flex gap-3 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.05] p-4 text-sm">
        <FileCheck2 className="mt-0.5 size-5 shrink-0 text-emerald-600" />
        <div><div className="font-medium">Campos de apresentação sincronizados</div><p className="mt-1 text-xs leading-relaxed text-muted-foreground">Inclusos, não inclusos, prazos, condições, escopo e observações agora permanecem vinculados ao mesmo rascunho. Se você apagar todos os itens de uma seção, ela continuará vazia.</p></div>
      </div>

      <div className="border border-border rounded-xl p-5 space-y-4" data-testid="presentation-controls">
        <div className="flex items-center gap-2">
          <Eye className="size-5 text-primary" />
          <div><h3 className="font-medium">Apresentação ao cliente</h3><p className="text-xs text-muted-foreground">Essas opções alteram apenas a apresentação; o cálculo financeiro permanece inalterado.</p></div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <ToggleCard checked={!!data.exibir_precos_itens} onChange={(v) => setData((d) => ({ ...d, exibir_precos_itens: v }))} icon={BadgeDollarSign} title="Exibir preço em cada item" description="Mostra o valor comercial junto aos serviços incluídos. Desmarcado mantém apenas o investimento total." disabled={hideFinancial} testid="toggle-item-prices" />
          <ToggleCard checked={!!data.acompanhamento?.exibir_num_funcionarios} onChange={setAcompPresentation} icon={Users} title="Exibir número de funcionários no acompanhamento" description="O número continua sendo usado para calcular ASO/eSocial, mesmo quando fica oculto na proposta." testid="toggle-show-employee-count" />
        </div>
        <div className="flex gap-2 rounded-md bg-muted/30 px-3 py-2 text-xs text-muted-foreground"><ShieldCheck className="size-4 text-primary shrink-0" />Itens efetivamente contratados são reconciliados com “Não inclusos” para evitar contradições no documento final.</div>
      </div>

      <div className="border border-border rounded-xl p-5 space-y-3" data-testid="review-escopo">
        <div className="flex flex-wrap items-center justify-between gap-2"><Label className="label-tech">Escopo / objetivo da proposta</Label><Button type="button" variant="outline" size="sm" disabled={aiBusy} onClick={() => gerarIA(["escopo"])} data-testid="ai-escopo-btn"><Sparkles className="size-4 mr-1" /> {aiBusy ? "Gerando..." : "Sugerir redação"}</Button></div>
        <Textarea rows={2} value={data.escopo_descricao || ""} placeholder="Vazio = o sistema gera o escopo automaticamente a partir dos serviços selecionados" onChange={(e) => setField("escopo_descricao", e.target.value)} data-testid="review-escopo-input" />
        {escopoSug && <p className="text-xs text-muted-foreground">Sugestão assistiva aplicada: “{escopoSug}”.</p>}
        <p className="text-xs text-muted-foreground">Este texto é exibido no site público e usado como objetivo/escopo no documento.</p>
      </div>

      {!hideFinancial && (
        <div className="grid md:grid-cols-2 gap-5">
          <div className="bg-muted/20 border border-border rounded-xl p-5" data-testid="review-summary"><div className="label-tech mb-2">Resumo financeiro interno</div><div className="grid grid-cols-2 gap-3 text-sm"><div><div className="text-muted-foreground text-xs">Data</div><div className="font-mono">{formatDateBR(data.data)}</div></div><div><div className="text-muted-foreground text-xs">Validade</div><div className="font-mono">{data.validade_dias} dias</div></div><div><div className="text-muted-foreground text-xs">Duração</div><div className="font-mono">{data.duracao_meses} meses</div></div><div><div className="text-muted-foreground text-xs">Parcelas</div><div className="font-mono">{data.num_parcelas}x</div></div><div className="col-span-2 border-t border-border pt-2 mt-2"><div className="text-muted-foreground text-xs">Valor Total</div><div className="font-mono text-2xl text-primary">{formatBRL(calc?.valor_total || 0)}</div></div><div className="col-span-2"><div className="text-muted-foreground text-xs">Valor por parcela</div><div className="font-mono text-lg">{formatBRL(calc?.valor_parcela || 0)}</div></div></div></div>
          <ListBlock testid="review-programas" title="I. Serviços, documentos e avaliações" items={[...programas, ...fixedIn, ...quimIn]} />
          <ListBlock testid="review-acompanhamento" title="II. Acompanhamento" items={acomp} />
          <ListBlock testid="review-bonus" title="III. Bônus" items={bonus} />
          {treinIn.length > 0 && <ListBlock testid="review-treinamentos-incl" title="Treinamentos inclusos" items={treinIn} />}
        </div>
      )}

      {hideFinancial && (
        <div className="grid md:grid-cols-2 gap-5"><ListBlock testid="review-programas" title="I. Serviços, documentos e avaliações" items={[...programas, ...fixedIn, ...quimIn]} /><ListBlock testid="review-acompanhamento" title="II. Acompanhamento" items={acomp} /><ListBlock testid="review-bonus" title="III. Bônus" items={bonus} /><ListBlock testid="review-treinamentos-incl" title="Treinamentos inclusos" items={treinIn} /></div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        <ListEditor testid="edit-inclusos" title="Inclusos" items={data.inclusos || []} onChange={(v) => setField("inclusos", v)} headerAction={aiBtn(["inclusos"])} />
        <ListEditor testid="edit-nao-inclusos" title="Não inclusos" items={data.nao_inclusos || []} onChange={(v) => setField("nao_inclusos", v)} hint="Se uma exclusão contradizer um serviço contratado, o backend a remove do documento final e mantém o escopo coerente." headerAction={aiBtn(["nao_inclusos"])} />
        <ListEditor testid="edit-prazos" title="Prazos" items={data.prazos || []} onChange={(v) => setField("prazos", v)} />
        <ListEditor testid="edit-condicoes" title="Condições comerciais" items={data.condicoes_comerciais || []} onChange={(v) => setField("condicoes_comerciais", v)} />
      </div>

      <div className="border border-border rounded-xl p-5 space-y-3" data-testid="review-observacoes">
        <div className="flex flex-wrap items-center justify-between gap-2"><Label className="label-tech">Observações gerais</Label><Button type="button" variant="outline" size="sm" disabled={aiBusy} onClick={() => gerarIA(["observacoes"])} data-testid="ai-observacoes-btn"><Sparkles className="size-4 mr-1" /> {aiBusy ? "Gerando..." : "Sugerir redação"}</Button></div>
        <Textarea rows={4} value={data.observacoes || ""} placeholder="Texto livre. Vazio = a seção Observações gerais não aparece na proposta." onChange={(e) => setField("observacoes", e.target.value)} data-testid="review-observacoes-input" />
        <p className="text-xs text-muted-foreground">Observações salvas aqui são exibidas no site público e na página de condições do documento.</p>
      </div>
    </div>
  );
}

